import { Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, ValidatorFn, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { ProjectConfiguration, ProjectId } from '@models/ProjectConfiguration';
import { combineLatest } from 'rxjs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SynchronizerService } from '../synchronizer.service';
import { CommonModule } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { MatListModule, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { CanonData, getCanon } from '@models/Canons';
import { VerseReference } from '@models/VerseReference';
import { Canon } from '@models/Canon';
import { HollowPublicationRequest } from '@models/PublicationRequest';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ApiService } from '../api.service';
import { BookIdentifier } from '@models/BookIdentifier';
import { PublicationStatusComponent } from './publication-status/publication-status.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PublicationConfigurationSelectorComponent } from "./publication-configuration-selector/publication-configuration-selector.component";
import { PublicationConfiguration } from '@models/PublicationConfiguration';

function parsingFormatSelected(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value.length > 0 ? null : { empty: true };
  };
}

@Component({
  selector: 'app-publication',
  imports: [CommonModule,
    MatInputModule,
    MatFormFieldModule,
    MatStepperModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatRadioModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatIconModule,
    PublicationStatusComponent,
    MatExpansionModule,
    MatCheckboxModule,
    PublicationConfigurationSelectorComponent],
  templateUrl: './publication.component.html',
  styleUrl: './publication.component.scss'
})
export class PublicationComponent implements OnInit {
  @ViewChildren(MatSelectionList) bookLists: QueryList<MatSelectionList> | undefined;
  public project: ProjectConfiguration | undefined;
  private _formBuilder = inject(FormBuilder);
  public selectedBooks: Set<BookIdentifier> = new Set<BookIdentifier>();
  public commitSha: string | undefined;

  public showPublishButton = true;
  public disablePublishButton = false;
  public showFallbackPublicationResult = false;
  public showTimeoutPublicationResult = false;
  public selectedPublicationConfigurationId: string = ProjectConfiguration.Default;

  apiResults: { [key: string]: { status: 'loading' | 'success' | 'failure', message?: string } } = {};

  parsingFormatFormGroup = this._formBuilder.group({});
  chooseBooksFormGroup = this._formBuilder.group({});
  advancedOptionsFormGroup = this._formBuilder.group({
    nopdf: new FormControl(false),
  });

  constructor(
    private synchronizerService: SynchronizerService,
    private route: ActivatedRoute,
    private api: ApiService
  ) { }

  ngOnInit() {
    combineLatest([this.route.paramMap, this.synchronizerService.user$]).subscribe(([params]) => {
      const id = params.get('projectid') as ProjectId;
      const p = this.synchronizerService.user$.value?.project(id);
      if (p) {
        this.project = p;
        this.populateFormControls();
      }
    });
  }

  populateFormControls() {
    if (this.project) {
      /// Selecting the parsing formats
      this.parsingFormatFormGroup.addControl('latexTemplate', this._formBuilder.control(null));

      this.project.canons.forEach(canon => {
        this.parsingFormatFormGroup.addControl(canon, this._formBuilder.control('', parsingFormatSelected()));
      });

      /// Select the only format, if there is an only format
      this.project.canons.forEach(canon => {
        const n = this.project?.parsingFormats.getNumberOfConfigurations(canon);
        if (n === 1) {
          const formatsIterator = this.project?.parsingFormats.getParsingFormatsForCanon(canon)?.values();
          if (formatsIterator) {
            const firstFormat = formatsIterator.next().value;
            if (firstFormat) {
              const patch: { [key: string]: string } = {};
              patch[canon] = firstFormat.id;
              this.parsingFormatFormGroup.patchValue(patch);
            }
          }
        }
      });

      /// Selecting the books
      this.project.canons.forEach(canon => {
        getCanon(canon).books.forEach((book) => {
          this.chooseBooksFormGroup.addControl(book, this._formBuilder.control(false));
        });
      });

    }
  }

  demoChangeNumerals(str: string) {
    const numerals = this.project?.numerals;
    if (numerals === undefined || numerals.length != 10) {
      return "error";
    } else {
      for (let i = 0; i < 10; i++) {
        str = str.replace(new RegExp(i.toString(), "g"), numerals[i]);
      }
      return str;
    }
  }

  canonData(canon: Canon): CanonData {
    return getCanon(canon);
  }

  async publish() {
    const request = this.publicationRequest;

    console.log('Request', JSON.stringify(request));

    if (request) {
      this.disablePublishButton = true;
      try {
        const result = await this.api.publish(request);
        /// if anything goes wrong it will not return 200
        this.showPublishButton = false;
        this.commitSha = result.object.sha;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        console.error('Error', e);
        if (e.status && e.status === 408) {
          this.showTimeoutPublicationResult = true;
        } else {
          this.showFallbackPublicationResult = true;
        }
      }
    }
  }

  get publicationRequest(): HollowPublicationRequest | undefined {
    if (this.project !== undefined) {

      const nopdf = this.advancedOptionsFormGroup.get('nopdf')?.value || false;

      const PublicationConfiguration = this.project.publicationConfigurations.get(this.selectedPublicationConfigurationId);
      if (!PublicationConfiguration) {
        return undefined;
      }

      return {
        books: [...this.selectedBooks].map(b => b.toObject()),
        project_id: this.project.id,
        nopdf: nopdf,
        publication_configuration_id: this.selectedPublicationConfigurationId,
      };
    } else {
      return undefined;
    }
  }

  get atLeastOneSelected(): boolean {
    return this.selectedBooks.size > 0;
  }

  booksSelected(event: MatSelectionListChange) {
    event.options.forEach((o) => {
      const bookId = BookIdentifier.fromString(o.value);
      if (bookId) {
        if (o.selected) {
          this.selectedBooks.add(bookId);
        } else {
          // this.selectedBooks.delete(bookId);
          this.selectedBooks.forEach((b) => {
            if (b.canon === bookId.canon && b.book === bookId.book) {
              this.selectedBooks.delete(b);
            }
          });
        }
      }
    });
  }

  get numberOfChaptersSelected(): number {
    let total = 0;
    this.selectedBooks.forEach(bid => {
      const canonData = getCanon(bid.canon);
      total += canonData.lastChapter(bid.book) || 0;
    });
    return total;
  }

  async makeApiCalls(): Promise<void> {
    this.selectedBooks.forEach(b => this.apiResults[b.toString()] = { status: 'loading' });

    const request = this.publicationRequest;
    if (request) {
      const results = await this.api.performCheck(request);
      this.apiResults = {};
      for (const bookIdString in results) {
        if (Object.hasOwn(results, bookIdString)) {
          const bookResults = results[bookIdString];
          if (bookResults.length === 0) {
            this.apiResults[bookIdString] = { status: 'success' };
          } else {
            this.apiResults[bookIdString] = {
              status: 'failure',
              message: "Missing glosses: " + bookResults.map(s => {
                const ref = VerseReference.fromString(s);
                return `${ref?.chapter}:${ref?.verse}`;
              }).join(', ')
            };
          }
        }
      }
    }
  }

  get someBookFailsCheck(): boolean {
    return Object.values(this.apiResults).some(r => r.status === 'failure');
  }

  localizedNameFromBookId(bookId: BookIdentifier): string {
    const book = bookId.book;
    if (book && this.project) {
      return this.project.getBookName(book);
    }
    return "Unknown";
  }

  selectAllBooks() {
    if (this.bookLists) {
      this.bookLists.forEach((list) => {
        list.selectAll();
        list.selectedOptions.selected.forEach((o) => {
          const bookId = BookIdentifier.fromString(o.value);
          if (bookId) {
            this.selectedBooks.add(bookId);
          }
        }
        );
      });
    }
  }

  selectNoBooks() {
    if (this.bookLists) {
      this.bookLists.forEach((list) => {
        list.deselectAll();
      });
    }
    this.selectedBooks.clear();
  }

  onConfigurationSelected(id: string) {
    this.selectedPublicationConfigurationId = id;
  }

  get publicationConfiguration(): PublicationConfiguration | undefined {
    return this.project?.publicationConfigurations.get(this.selectedPublicationConfigurationId);
  }

  get possibleToPublishWithCurrentPublicationConfiguration(): boolean {
    if (this.publicationConfiguration) {
      return this.publicationConfiguration.canonsWithParsingFormats.length > 0;
    }
    return false;
  }

  get canonsWithParsingFormats(): string {
    if (this.publicationConfiguration) {
      return this.publicationConfiguration.canonsWithParsingFormats.map(c => c.toString()).join(', ');
    }
    return "";
  }

  get canonsWithoutParsingFormats(): string {
    if (this.publicationConfiguration) {
      return this.publicationConfiguration.canonsWithoutParsingFormats.map(c => c.toString()).join(', ');
    }
    return "";
  }

}
